import React, { useState, useEffect } from "react"

import { useStaticQuery, graphql } from "gatsby"
import PropTypes from "prop-types"

import * as styles from "./social.module.scss"
import Analytics from "../../../helpers/analytics"
import FormField from "../../atoms/form-field"
import Icon from "../../atoms/icon"

const Social = ({
  shareLink,
  story,
  className,
  light,
  spaced,
  shareMessage,
  shareTitle,
  showAllIcons,
  size,
}) => {
  const { site, allSanitySocial, allSanityDictionary } = useStaticQuery(graphql`
    {
      site {
        siteMetadata {
          siteUrl
        }
      }
      allSanityDictionary {
        nodes {
          key {
            current
          }
          value
        }
      }
      allSanitySocial(sort: { name: ASC }) {
        nodes {
          id
          name
          image {
            ...ImageWithPreview
          }
          dark_image {
            ...ImageWithPreview
          }
          share_url
          urls {
            url
            country {
              url
            }
          }
          shareable
        }
      }
    }
  `)
  const [isCopied, setIsCopied] = useState(false)
  const [canUseNavigator, setCanUseNavigator] = useState(false)

  useEffect(() => {
    if (navigator.share) setCanUseNavigator(true)
  }, [])

  const trackClick = (e) => {
    Analytics.triggerEvent(
      "click_social",
      { social: e.target.dataset.icon },
      "ClickSocial"
    )
  }

  const copyToClipboard = () => {
    const linkInput = document.getElementById("copy__link")
    linkInput.select()
    document.execCommand("copy")
    setIsCopied(true)
  }

  const activateShare = async () => {
    const shareData = {
      url: shareLink,
    }
    if (shareTitle) shareData.title = shareTitle
    if (shareMessage) shareData.text = shareMessage

    await navigator.share(shareData)
  }

  const socialList = allSanitySocial.nodes.map((item) => (
    <a
      key={item.id}
      href={
        item.urls.filter(
          (url) =>
            url.country === null ||
            url.country.url === site.siteMetadata.siteUrl
        )[0].url
      }
      className={`${styles.icon} ${light ? styles.icon__light : ""}`}
    >
      <Icon category="brand" icon={item.name} image={item.image} size={size} />
    </a>
  ))

  const shareList =
    canUseNavigator && !showAllIcons ? (
      <div
        role="button"
        tabIndex="0"
        onClick={activateShare}
        onKeyPress={activateShare}
        className={`${styles.icon} ${light ? styles.icon__light : ""}`}
      >
        <Icon icon="share" light={light} size={size} />
      </div>
    ) : (
      <>
        {allSanitySocial.nodes.map(
          (item) =>
            !item.shareable || (
              <a
                target="_blank"
                rel="noopener noreferrer"
                key={item.id}
                data-icon={item.name}
                onClick={trackClick}
                href={
                  item.share_url +
                  encodeURIComponent(
                    !shareMessage || story ? "" : shareMessage
                  ) +
                  encodeURIComponent(shareLink)
                }
                className={`${styles.icon} ${light ? styles.icon__light : ""}`}
              >
                <Icon
                  category="brand"
                  icon={item.name}
                  image={light ? item.image : item.dark_image}
                  standalone
                  light={light}
                  size={size}
                />
              </a>
            )
        )}
        <div
          role="button"
          tabIndex="0"
          onClick={copyToClipboard}
          onKeyPress={copyToClipboard}
          className={`${styles.icon} ${light ? styles.icon__light : ""}`}
        >
          <div className={styles.hiddenInput}>
            <FormField form="copy" field="link" value={shareLink} readOnly />
          </div>
          <Icon icon="link" />
          {isCopied === true ? (
            <span className={styles.copied}>
              &nbsp;
              {
                allSanityDictionary.nodes.find(
                  (item) => item.key.current === "copied"
                )?.value
              }
            </span>
          ) : (
            ""
          )}
        </div>
      </>
    )

  return (
    <div
      className={`${styles.social} ${
        spaced ? styles.social__spaced : ""
      } ${className}`}
    >
      {shareLink ? (
        <>
          {!showAllIcons ? (
            <div className={styles.title}>
              {
                allSanityDictionary.nodes.find(
                  (item) => item.key.current === "share"
                )?.value
              }
            </div>
          ) : (
            ""
          )}
          {shareList}
        </>
      ) : (
        socialList
      )}
    </div>
  )
}

export default Social

Social.propTypes = {
  shareLink: PropTypes.string,
  shareMessage: PropTypes.string,
  shareTitle: PropTypes.string,
  story: PropTypes.bool,
  className: PropTypes.string,
  light: PropTypes.bool,
  spaced: PropTypes.bool,
  showAllIcons: PropTypes.bool,
  size: PropTypes.string,
}

Social.defaultProps = {
  shareLink: "",
  shareMessage: null,
  shareTitle: null,
  story: false,
  className: "",
  light: false,
  spaced: false,
  showAllIcons: false,
  size: "large",
}
